import React, { useRef } from 'react';
import { PageHeader } from '../components/PageHeader';
import { useTheme } from '../components/ThemeContext';
import SEO from '../components/SEO';
import GradientText from '@/components/GradientText';
import tutorialVideo from '../assets/tutorial/Creator_Flow_Script_01.mp4';

interface TranscriptItem {
    time: number;
    text: string;
}

const transcriptData: TranscriptItem[] = [
    { time: 3, text: "Let's begin by signing into the Quilt web app." },
    { time: 6, text: "Enter your email address followed by the password you've received, and click login." },
    { time: 11, text: "Once you're in, you'll see a list of all your approved videos." },
    { time: 14, text: "Now let's create our first batch of videos." },
    { time: 18, text: "Navigate to the generation tab." },
    { time: 20, text: "Your clinic name will already be selected." },
    { time: 23, text: "Click the submit button to start the workflow." },
    { time: 27, text: "The system will process your request and generate 5 unique video ideas." },
    { time: 32, text: "Once ready, you can review them by clicking to expand each idea." },
    { time: 36, text: "Select the ideas you'd like to proceed with by checking the box next to each one." },
    { time: 41, text: "For this example, we'll select 3 ideas, but you can choose up to 5." },
    { time: 45, text: "If none of the ideas meet your requirements, you can click reject all." },
    { time: 50, text: "After making your selection, click submit to continue." },
    { time: 54, text: "You'll now see the progress of each selected idea." },
    { time: 57, text: "The progress bar will update as each stage is completed, such as image generation, music, narration, and captions." },
    { time: 65, text: "Completed stages will be clearly highlighted, once a video is ready, it will be available for review." },
    { time: 71, text: "You can watch the video and choose to either approve or reject it." },
    { time: 115, text: "At the crosswalk, notice your face." },
    { time: 118, text: "Let your jaw soften, your forehead ease, and your lips rest into a gentle half smile." },
    { time: 125, text: "No need to force happiness, just a small release." },
    { time: 129, text: "As your expression relaxes, your breath can slow, and your body may begin to feel steadier, calmer, and more at ease." },
    { time: 139, text: "Let's reject this video and provide a reason for rejection." },
    { time: 149, text: "Now the next video is ready for review." },
    { time: 151, text: "Let's take a look." },
    { time: 153, text: "Before a stressful moment, notice your face." },
    { time: 156, text: "Unclench your jaw, soften your eyes, and let one corner of your mouth lift into a gentle half smile." },
    { time: 163, text: "It doesn't force happiness." },
    { time: 165, text: "It simply tells your body you're safe enough to relax." },
    { time: 169, text: "In this quiet pause, your breath steadies, your shoulders ease, and you step forward feeling less guarded and more ready." },
    { time: 182, text: "To approve a video, select approve, add a brief description, and include relevant tags." },
    { time: 192, text: "You can type a tag and press enter to add it." },
    { time: 204, text: "Once done, click submit." },
    { time: 208, text: "Repeat the same process to approve or reject the remaining videos." },
    { time: 216, text: "Once all videos are reviewed, the workflow is complete." },
    { time: 220, text: "You can start a new batch anytime." },
    { time: 222, text: "You can view the status of batches in the history tab." },
    { time: 226, text: "To view your approved videos, simply go to the my video section." }
];

const AutomatedVideoCreation: React.FC = () => {
    const { theme } = useTheme();
    const videoRef = useRef<HTMLVideoElement>(null);

    const handleTimestampClick = (time: number) => {
        if (videoRef.current) {
            videoRef.current.currentTime = time;
            videoRef.current.play();
            // Scroll video into view with some padding
            videoRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <div className="min-h-screen bg-white dark:bg-black text-slate-900 dark:text-white flex flex-col">
            <SEO
                title="Automated Video Creation Pipeline | Quilt"
                description="Learn how Quilt automates high-quality video content production for health and wellness patient communication."
                path="/getting-started/automated-video"
            />
            <PageHeader
                navItems={[
                    { label: "For You", href: "/" },
                    { label: "For Providers", href: "/healthcare" }
                ]}
            />

            <main className="flex-1 w-full flex flex-col items-center pt-32 pb-20 px-6">
                <div className="relative w-full max-w-6xl mx-auto">

                    <div className="relative z-10 flex flex-col items-center text-center mb-16">
                        <GradientText className="text-xl font-medium mb-4" animationSpeed={3}>
                            Getting started
                        </GradientText>
                        <h1 className="text-4xl md:text-6xl font-light tracking-tight mb-8">
                            Automated Video Creation
                        </h1>
                        <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl font-light">
                            Learn how to use Quilt's generative engine to effortlessly create video content for your patients.
                        </p>
                    </div>

                    <div className="relative z-10 w-full max-w-4xl mx-auto mb-20 group">
                        {/* Decorative background for the video */}
                        <div className="absolute -inset-4 bg-gradient-to-tr from-purple-500/10 via-fuchsia-500/10 to-blue-500/10 blur-2xl opacity-50 group-hover:opacity-100 transition-opacity duration-500 rounded-[2rem]" />

                        <div className="relative bg-slate-100 dark:bg-zinc-900 rounded-2xl overflow-hidden shadow-2xl border border-slate-200 dark:border-zinc-800">
                            <video
                                ref={videoRef}
                                src={tutorialVideo}
                                controls
                                playsInline
                                className="w-full h-auto object-cover"
                            >
                                Your browser does not support the video tag.
                            </video>
                        </div>
                    </div>

                    <div className="relative z-10 w-full max-w-3xl mx-auto">
                        <div className="flex items-center gap-4 mb-8">
                            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-200 dark:via-zinc-800 to-transparent" />
                            <h2 className="text-2xl font-light text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                                Transcript
                            </h2>
                            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-200 dark:via-zinc-800 to-transparent" />
                        </div>

                        <div className="prose prose-slate dark:prose-invert max-w-none text-sm">
                            <div className="bg-slate-50/50 dark:bg-zinc-950/50 border border-slate-100 dark:border-zinc-900 rounded-2xl p-6 md:p-10 space-y-6">
                                {transcriptData.map((item, index) => (
                                    <div
                                        key={index}
                                        className="flex gap-4 md:gap-6 group/item cursor-pointer hover:bg-slate-100/50 dark:hover:bg-zinc-900/50 py-2.5 px-2 -mx-2 -my-1 rounded-xl transition-colors"
                                        onClick={() => handleTimestampClick(item.time)}
                                    >
                                        <button
                                            type="button"
                                            className="font-mono text-purple-600 dark:text-slate-400 font-bold shrink-0 self-start hover:scale-110 transition-transform"
                                            title={`Jump to ${formatTime(item.time)}`}
                                        >
                                            {formatTime(item.time)}
                                        </button>
                                        <p className="text-slate-700 dark:text-slate-500 leading-relaxed">
                                            {item.text}
                                        </p>
                                    </div>
                                ))}

                                <div className="mt-8 pt-8 border-t border-slate-100 dark:border-zinc-900 flex justify-center">
                                    <p className="text-sm text-slate-500 dark:text-slate-600 font-mono">
                                        End of transcript.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default AutomatedVideoCreation;
